import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { usePlacementTransitionInputs, PlacementTransitionEntry } from '@/hooks/usePlacementTransitionInputs';

// Color coding for values
const getValueColor = (value: string): string => {
  switch (value.toLowerCase()) {
    case 'yes':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'no':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

// Value Badge Component
interface ValueBadgeProps {
  value: string;
}

const ValueBadge: React.FC<ValueBadgeProps> = React.memo(({ value }) => {
  if (!value || value === '-') {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <Badge variant="secondary" className={getValueColor(value)}>
      {value}
    </Badge>
  );
});

ValueBadge.displayName = 'ValueBadge';

// Entry Row Component
interface EntryRowProps {
  entry: PlacementTransitionEntry;
  index: number;
}

const EntryRow: React.FC<EntryRowProps> = React.memo(({ entry, index }) => (
  <TableRow className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
    <TableCell className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 break-words">
      {entry.field}
    </TableCell>
    <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-center w-24">
      <ValueBadge value={entry.value} />
    </TableCell>
  </TableRow>
));

EntryRow.displayName = 'EntryRow';

// Loading Row Component
const LoadingRow: React.FC = React.memo(() => (
  <TableRow>
    <TableCell className="px-4 py-3">
      <Skeleton className="h-4 w-full max-w-md" />
    </TableCell>
    <TableCell className="px-4 py-3 text-center">
      <Skeleton className="h-6 w-16 mx-auto" />
    </TableCell>
  </TableRow>
));

LoadingRow.displayName = 'LoadingRow';

// Main Dashboard Component
const PlacementTransitionInputsDashboard: React.FC = () => {
  const { selectedProduct } = usePMT3Selection();
  const { data, isLoading, error } = usePlacementTransitionInputs(
    selectedProduct?.companyName,
    selectedProduct?.id
  );

  return (
    <div className="flex justify-center">
      <Card className="w-full max-w-3xl">
        <CardHeader className="p-4">
          <CardTitle className="text-2xl font-bold text-center">Placement Transition Inputs</CardTitle>
        </CardHeader>
        <CardContent className="pb-6">
          {error && (
            <div className="text-red-600 dark:text-red-400 mb-4">
              Error loading data: {error.message}
            </div>
          )}
          
          <div className="overflow-x-auto">
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap text-xs py-2 px-4 font-bold">
                    Item
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-xs py-2 px-4 font-bold text-center w-24">
                    Value
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Show loading skeletons for each field
                  Array.from({ length: 5 }, (_, index) => (
                    <LoadingRow key={`loading-${index}`} />
                  ))
                ) : data && data.length > 0 ? (
                  // Show actual data
                  data.map((entry, index) => (
                    <EntryRow 
                      key={entry.field} 
                      entry={entry} 
                      index={index}
                    />
                  ))
                ) : (
                  // No data state
                  <TableRow>
                    <TableCell 
                      colSpan={2} 
                      className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      {selectedProduct 
                        ? 'No placement transition inputs found for this product.'
                        : 'Please select a product to view placement transition inputs.'
                      }
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlacementTransitionInputsDashboard;